import React, { useState } from "react";
import {
  GripVertical, MoreVertical, Music2, Search, Sparkles, Star, X,
  Pencil, Trash2, HelpCircle, Ban, Check, ExternalLink, ChevronDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PreviewButton } from "@/components/PreviewButton";
import { EXECUTION_OPTIONS, SURPRISE_FOR } from "@/lib/constants";

export const MomentCard = ({
  moment, index, onChange, onDelete, onSearch, onAssistant,
  dragHandleProps, isPending, structural = true,
}) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(moment.name);
  const [showNotes, setShowNotes] = useState(!!moment.observations);

  const upd = (patch) => onChange({ ...moment, ...patch });
  const music = moment.music || {};
  const hasMusic = !!music.track_name;
  const hasManual = !hasMusic && !!moment.manual_link;

  const saveName = () => {
    if (name.trim()) upd({ name: name.trim() });
    setEditing(false);
  };

  return (
    <div
      data-testid={`moment-card-${moment.id}`}
      className={`rounded-2xl border bg-white shadow-[0_2px_10px_rgba(20,19,22,0.04)] transition-shadow ${
        isPending ? "border-[color:var(--brand-marsala)] ring-1 ring-[color:var(--brand-marsala)]" : "border-[color:var(--brand-border)]"
      }`}
    >
      {/* header */}
      <div className="flex items-center gap-2 border-b border-[color:var(--brand-border)] p-3.5">
        {structural && (
          <button
            {...dragHandleProps}
            data-testid={`moment-drag-${moment.id}`}
            className="cursor-grab touch-none text-[color:var(--brand-muted)] active:cursor-grabbing"
            title="Arraste para reordenar"
          >
            <GripVertical className="h-5 w-5" />
          </button>
        )}
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[color:var(--brand-paper)] text-xs font-semibold text-[color:var(--brand-muted)]">
          {index + 1}
        </span>
        {editing ? (
          <div className="flex flex-1 items-center gap-2">
            <Input
              data-testid={`moment-name-input-${moment.id}`}
              value={name}
              autoFocus
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && saveName()}
              className="h-8 flex-1"
            />
            <Button size="sm" onClick={saveName} className="h-8 bg-[color:var(--brand-marsala)] text-white">
              <Check className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <h3 className="flex-1 font-serif text-lg font-semibold text-[color:var(--brand-graphite)]">
            {moment.name}
          </h3>
        )}

        {moment.surprise?.is && (
          <span className="inline-flex items-center gap-1 rounded-full bg-[color:var(--brand-marsala)] px-2 py-0.5 text-[10px] font-semibold text-white">
            <Star className="h-3 w-3 fill-white" /> Surpresa
          </span>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid={`moment-menu-${moment.id}`} className={`rounded-lg p-1.5 text-[color:var(--brand-muted)] hover:bg-[color:var(--brand-paper)] ${structural ? "" : "hidden"}`}>
              <MoreVertical className="h-5 w-5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setEditing(true)} data-testid={`moment-rename-${moment.id}`}>
              <Pencil className="mr-2 h-4 w-4" /> Renomear momento
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onDelete} className="text-[color:var(--brand-danger)]" data-testid={`moment-delete-${moment.id}`}>
              <Trash2 className="mr-2 h-4 w-4" /> Excluir momento
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* body */}
      {moment.not_applicable ? (
        <div className="flex items-center justify-between p-4">
          <span className="inline-flex items-center gap-2 text-sm text-[color:var(--brand-muted)]">
            <Ban className="h-4 w-4" /> Este momento não se aplica à cerimônia.
          </span>
          <Button variant="outline" size="sm" onClick={() => upd({ not_applicable: false })} className="h-8">
            Reativar
          </Button>
        </div>
      ) : (
        <div className="space-y-4 p-4">
          {/* music */}
          {hasMusic ? (
            <div className="flex items-center gap-3 rounded-xl border border-[color:var(--brand-border)] bg-[color:var(--brand-paper)] p-2.5">
              {music.artwork ? (
                <img src={music.artwork} alt="" className="h-14 w-14 rounded-lg object-cover" />
              ) : (
                <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-white">
                  <Music2 className="h-6 w-6 text-[color:var(--brand-muted)]" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-[color:var(--brand-graphite)]">{music.track_name}</p>
                <p className="truncate text-xs text-[color:var(--brand-muted)]">{music.artist_name}{music.duration ? ` · ${music.duration}` : ""}</p>
                <div className="mt-1.5 flex items-center gap-2">
                  <PreviewButton url={music.preview_url} testId={`moment-preview-${moment.id}`} label />
                  {music.link && (
                    <a href={music.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[color:var(--brand-marsala)] hover:underline">
                      <ExternalLink className="h-3 w-3" /> Abrir
                    </a>
                  )}
                </div>
              </div>
              <button data-testid={`moment-remove-music-${moment.id}`} onClick={() => upd({ music: null })} className="rounded-full p-1 text-[color:var(--brand-muted)] hover:bg-white" title="Remover">
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : hasManual ? (
            <div className="flex items-center justify-between rounded-xl border border-[color:var(--brand-border)] bg-[color:var(--brand-paper)] p-3">
              <a href={moment.manual_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 truncate text-sm text-[color:var(--brand-marsala)] hover:underline">
                <ExternalLink className="h-4 w-4 shrink-0" /> <span className="truncate">{moment.manual_link}</span>
              </a>
              <button onClick={() => upd({ manual_link: "" })} className="rounded-full p-1 text-[color:var(--brand-muted)] hover:bg-white">
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div>
              <p className="text-sm font-medium text-[color:var(--brand-graphite)]">Qual música vocês escolheram?</p>
              <p className="mb-2 text-xs text-[color:var(--brand-muted)]">* Busque pelo nome ou artista e confirme a versão. Sem ideia? Toque em "ver sugestões".</p>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button
                  data-testid={`moment-search-button-${moment.id}`}
                  onClick={onSearch}
                  className="h-11 flex-1 bg-[color:var(--brand-marsala)] text-white hover:bg-[color:var(--brand-marsala-hover)]"
                >
                  <Search className="mr-2 h-4 w-4" /> Buscar música
                </Button>
                <Button
                  data-testid={`moment-ai-suggest-button-${moment.id}`}
                  variant="outline"
                  onClick={onAssistant}
                  className="h-11 flex-1 border-[color:var(--brand-champagne)] text-[color:var(--brand-graphite)]"
                >
                  <Sparkles className="mr-2 h-4 w-4 text-[color:var(--brand-marsala)]" /> Não sei — ver sugestões
                </Button>
              </div>
            </div>
          )}

          {/* manual reference link when a track is selected */}
          {(hasMusic || hasManual) && (
            <div>
              <label className="text-xs font-medium text-[color:var(--brand-muted)]">Link de referência (opcional — versão exata)</label>
              <Input
                data-testid={`moment-ref-link-${moment.id}`}
                value={moment.manual_link || ""}
                onChange={(e) => upd({ manual_link: e.target.value })}
                placeholder="Cole aqui a versão exata (YouTube, Spotify, Apple)"
                className="mt-1 h-10"
              />
            </div>
          )}

          {/* execution */}
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="text-xs font-medium text-[color:var(--brand-muted)]">Como usar a música?</label>
              <Select value={moment.execution_type || ""} onValueChange={(v) => upd({ execution_type: v })}>
                <SelectTrigger data-testid={`moment-execution-${moment.id}`} className="mt-1 h-10">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {EXECUTION_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {moment.execution_type === "especifico" && (
              <div>
                <label className="text-xs font-medium text-[color:var(--brand-muted)]">Informe o trecho</label>
                <Input
                  data-testid={`moment-execution-detail-${moment.id}`}
                  value={moment.execution_detail || ""}
                  onChange={(e) => upd({ execution_detail: e.target.value })}
                  placeholder="Ex: começar em 01:14 / no segundo refrão"
                  className="mt-1 h-10"
                />
              </div>
            )}
          </div>

          {/* surprise */}
          <div className="rounded-xl border border-[color:var(--brand-border)] p-3">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm text-[color:var(--brand-graphite)]">
                <Star className="h-4 w-4 text-[color:var(--brand-marsala)]" /> Esta música é surpresa para alguém?
              </span>
              <Switch
                data-testid={`moment-surprise-toggle-${moment.id}`}
                checked={!!moment.surprise?.is}
                onCheckedChange={(v) => upd({ surprise: { is: v, for_whom: v ? moment.surprise?.for_whom || "" : "" } })}
              />
            </div>
            {moment.surprise?.is && (
              <div className="mt-3">
                <label className="text-xs font-medium text-[color:var(--brand-muted)]">Surpresa para quem? (opcional)</label>
                <Select value={moment.surprise?.for_whom || ""} onValueChange={(v) => upd({ surprise: { is: true, for_whom: v } })}>
                  <SelectTrigger data-testid={`moment-surprise-for-${moment.id}`} className="mt-1 h-10">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {SURPRISE_FOR.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* notes */}
          {showNotes ? (
            <div>
              <label className="text-xs font-medium text-[color:var(--brand-muted)]">Observações</label>
              <Textarea
                data-testid={`moment-notes-${moment.id}`}
                value={moment.observations || ""}
                onChange={(e) => upd({ observations: e.target.value })}
                placeholder="Algo que a equipe deva saber sobre este momento"
                className="mt-1 min-h-[70px]"
              />
            </div>
          ) : (
            <button onClick={() => setShowNotes(true)} className="text-xs font-medium text-[color:var(--brand-muted)] hover:text-[color:var(--brand-graphite)]">
              + Adicionar observação
            </button>
          )}

          {/* footer states */}
          <div className="flex flex-wrap items-center gap-2 border-t border-[color:var(--brand-border)] pt-3">
            {!hasMusic && !hasManual && (
              <button
                data-testid={`moment-undecided-${moment.id}`}
                onClick={() => upd({ undecided: !moment.undecided })}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors ${
                  moment.undecided
                    ? "border-[color:var(--brand-warning)] bg-[#FBF3E4] text-[color:var(--brand-warning)]"
                    : "border-[color:var(--brand-border)] text-[color:var(--brand-muted)] hover:bg-[color:var(--brand-paper)]"
                }`}
              >
                <HelpCircle className="h-3.5 w-3.5" /> {moment.undecided ? "Marcado: ainda não decidimos" : "Ainda não decidimos"}
              </button>
            )}
            <button
              data-testid={`moment-na-${moment.id}`}
              onClick={() => upd({ not_applicable: true })}
              className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--brand-border)] px-3 py-1.5 text-xs text-[color:var(--brand-muted)] transition-colors hover:bg-[color:var(--brand-paper)]"
            >
              <Ban className="h-3.5 w-3.5" /> Não se aplica
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
